import { useEffect, useState } from "react";
import { specials, type Special } from "@/mocks/portalData";

export default function Specials() {
  const [active, setActive] = useState<Special | null>(null);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section id="specials" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-medium text-primary-600">深度阅读 · IN-DEPTH</p>
          <h2 className="mt-1 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            热点背后的深度专题
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            不只看到「发生了什么」，更带你看懂事件背后的门道。点击卡片即可展开完整内容。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {specials.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s)}
              className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-100/60 text-left transition-colors hover:border-primary-300"
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  width={1200}
                  height={800}
                />
                <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-3 py-1 text-xs font-semibold text-primary-700">
                  {s.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-lg">
                  {s.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{s.summary}</p>
                <div className="mt-auto flex items-center justify-between pt-4">
                  <span className="flex items-center gap-2 text-xs text-foreground-400">
                    <i className="ri-file-text-line text-sm"></i>
                    {s.wordCount} · {s.readTime}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600">
                    阅读全文
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && <ReadingModal special={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function ReadingModal({ special, onClose }: { special: Special; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-foreground-950/50 p-0 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-background-50 sm:rounded-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-background-200/70 bg-background-50 px-5 py-3 md:px-7">
          <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            {special.tag}
          </span>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-5 py-6 md:px-8 md:py-8">
          <h3 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {special.title}
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
            <span>全站编辑部</span>
            <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
            <span>{special.wordCount}</span>
            <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
            <span>{special.readTime}</span>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl">
            <img src={special.image} alt={special.title} className="h-56 w-full object-cover md:h-72" width={1200} height={800} />
          </div>

          <div className="mt-6 space-y-4 text-[15px] leading-8 text-foreground-700">
            <p>{special.summary}</p>
            <p>
              当你刷到一条讨论量极高的内容时，往往意味着这件事已经突破单一圈层，进入更广泛的公共视野。
              我们对这类信息的处理原则是：先定位来源，再梳理脉络，最后才是观点表达。唯有回到一手信息，讨论才不至于变成无意义的情绪复读。
            </p>
            <p>
              因此，这篇专题想与你分享的不是「答案」，而是一套看待热点的方法——学会追问信息来自谁、有没有原始出处、当事人如何回应，比急着站队更有价值。
            </p>
            <p>
              我们也希望读者保持理性与善意。网络讨论的高温下，尊重每个人对同一事件的不同视角，才是让公共讨论保持健康的关键。
            </p>
            <div className="rounded-xl bg-background-100 p-4 text-sm leading-7 text-foreground-600">
              <span className="font-semibold text-primary-700">内容提示：</span>
              本文为编辑部基于公开讨论的观察整理，不构成对任何个人或组织的定性结论，事件最终以官方信息为准。
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}